import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';

import { Observable, Subject, combineLatest } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { SubjectRelay } from '../../../../shared/commands/subject-relay.class';

import { CategoriePdP, TraductionPdp } from '../../entities/traduction-pdp.entity';
import { PlanPreventionEntity } from "../../entities/plan-prevention.entity";
import { PlanPreventionService } from "../../services/plan-prevention.service";
import { TraductionPdpService } from '../../services/traduction-pdp.service';

import { v4 as uuidv4 } from 'uuid';

// Classe refletant les donnees de construction du composant passees par data-binding via le parent
class InitData {
	constructor(
		public type: string,
		public parentType: string,
		public highlightedSections: number[],
		public pdp: PlanPreventionEntity,
	) { }
}

type RefResponsibilities = {
	[id: number]: string;
}
@Component({
	selector: 'analyse-globale-pdp',
	templateUrl: 'analyse-globale-pdp.html',
	styleUrls: ['analyse-globale-pdp.css']
})
export class AnalyseGlobalePdpComponent {
	// Sujet servant a memoriser les inputs de construction dont on a besoin pour charger/evaluer des donnees
	private initSubject = new Subject<InitData>();

	// Variables internes (private par defaut)
	private selectedTraductionIds: number[] = [];

	// Variables bindees sur la vue
	traductions: TraductionPdp[] = [];
	poidsFemme: number = 15;
	poidsHomme: number = 25;
	pdpToCopyId: number = -1;

	constructor(
		@Inject(TraductionPdpService) private traductionPdpService: TraductionPdpService,
		@Inject(PlanPreventionService) private planPreventionService: PlanPreventionService ) {
		
		this.analyseGlobaleCommandsSubject.subscribe(
			// lors de la reception d'une demande d'affichage
			(pdp: PlanPreventionEntity) => {
				// Un fois les inputs de construction recus, on lance l'initialisation via le sujet ad-hoc
				// NB: a ce stade le code HTML du composant n'est pas encore construit et il ne faut pas utiliser les inputs qui dependent des actions utilisateur
				this.initSubject.next(new InitData(
					this.type,
					this.parentType,
					this.highlightedSections,
					pdp
				));
			}
		);
		
		this.pdpToCopySubject.subscribe(
			// lors de la reception d'une demande de duplication
			(pdpToCopyId: number) => {
				this.pdpToCopyId = pdpToCopyId;
			}
		);
		
		// Chargements de donnees qui ne dependent pas des Input pousses par le composant parent
		// exemple: let someData$ = this.someService.getSomeData();
		// NOOP
		
		// Chargements de donnees qui ne dependent que des Input de construction du composant (cf classe InitData)
		// exemple: let someData$ = this.initSubject.pipe(mergemap((initData: InitData) => this.someService.getSomeData(initData.xxx)));
		// Lors de l'appel de l'initialisation, on charge les traductions concernant le PDP ou un nouveau PDP
		let pdpTraductionsByEnergie$: Observable<Map<string, TraductionPdp[]>> = this.initSubject.pipe(
			mergeMap (
				(initData: InitData) => initData.pdp.id ?
					this.traductionPdpService.getTraductionsByPdP(initData.parentType, initData.pdp.id)
					:
					this.traductionPdpService.getTraductions(initData.parentType)
			),
			map(
				(traductions) => {
					let map = new Map<string, TraductionPdp[]>();
					
					let eolienneElems = this.filterTraductions(traductions, [CategoriePdP.MIXTE, CategoriePdP.EOL]);
					map.set("Eolienne", eolienneElems);
					
					let solaireElems = this.filterTraductions(traductions, [CategoriePdP.MIXTE, CategoriePdP.PV]);
					map.set("Solaire", solaireElems);
										
					return map;
				}
			)
		);
		
		// Chargements de donnees qui dependent des valeurs modifiable par les actions utilisateur sur d'autre composants
		// exemple: let someData$ = this.someActionDataSubject.pipe(mergeMap((someActionData) => this.someService.getSomeData(someActionData)));
		// NOOP
		
		// Combinaison des donnees Input et donnes chargees pour obtenir les valeurs des variables non-Input bindees sur la vue
		// exemple: combineLatest([someData$, someOtherData$]).subscribe((data) => { ... });
		// Il peut y avoir plusieurs combinaisons necessaires, eviter de tenter de faire une seule grosse combinaison
		let pdpTraductionsAllowedForEnergy$ = combineLatest([pdpTraductionsByEnergie$, this.energySubject]).pipe(
			map(
				(data) => {
					let pdpTraductionByEnergie = data[0];
					let energy = data[1];
					
					if (!energy) {
						return [];
					}
					
					let pdpTraductions = pdpTraductionByEnergie.get(energy);
					if (!pdpTraductions) {
						throw new Error("Energie non-reconnue: " + energy);
					}
					
					return pdpTraductions;
				}
			)
		)
		combineLatest([this.initSubject, pdpTraductionsAllowedForEnergy$]).subscribe(
			(data) => {
				let initData = data[0]
				let traductions = data[1];

				if (initData.pdp.id) {
					// chargement donnees existantes
					if (initData.type === 'RISK') {
						this.loadRisksForExistingPdp(this.pdpToCopyId != -1 ? this.pdpToCopyId : initData.pdp.id, traductions);
					} else if (initData.type === 'IMPACT') {
						this.loadImpactsForExistingPdp(this.pdpToCopyId != -1 ? this.pdpToCopyId : initData.pdp.id, traductions);
					}
				} else {
					this.traductions = traductions;
					this.traductions.forEach(traduction => {
						traduction.activites = traduction.children.filter($ => $.type === 'ACTIVITE');
						traduction.riskOrImpacts = traduction.children.filter($ => $.type === this.type);
						traduction.mesures = traduction.children.filter($ => $.type === 'MESURE_PREVENTION');
					});
					this.onModelChange.emit(this.traductions);
				}
			}
		);
	}
	
	private filterTraductions(traductions: TraductionPdp[], categories: CategoriePdP[]): TraductionPdp[] {
		let traductionsFiltrees: TraductionPdp[] = [];
		for (let traduction of traductions) {
			if (this.matchCategories(traduction, categories)) {
				// Si la traduction correspond aux categories retenues, creer une copie pour pouvoir contenir une liste d'enfants filtrees
				let clone: TraductionPdp = { ...traduction };
				
				let filteredChildren = traduction.children.filter(child => this.matchCategories(child, categories));
				clone.children = filteredChildren;
				
				traductionsFiltrees.push(clone);
			}
		}
		return traductionsFiltrees;
	}
	
	private matchCategories(traduction: TraductionPdp, categories: CategoriePdP[]): boolean {
		return categories.findIndex(c => c === traduction.categoriePdp) > -1;
	}
	
	// Inputs de construction du composants (ne peuvent etre modifies par les actions utilisateur durant la vie de l'instant du composant)
	// Part 1 - valeurs non-utilises pour le chagrements de donnes et directement bind sur les sous-composants dans la vue
	@Input() label1!: string;
	@Input() label2!: string;
	// Part 2 - valeurs servant aux chargement de donnees de context depuis le serveur et/ou a l'evaluation de donnees finalement utilisees dans la vue
	// (NB: ces donnees devraient avoir une correspondance dans la classe InitData)
	@Input() type: string = '';
	@Input() parentType!: string;
	@Input() highlightedSections: number[] = [];

	private analyseGlobaleCommandsSubject = new SubjectRelay<PlanPreventionEntity>();
	@Input() set analyseGlobaleCommands(obs: Observable<PlanPreventionEntity>) {
		this.analyseGlobaleCommandsSubject.registerSource(obs);
	}

	private pdpToCopySubject = new SubjectRelay<number>();
	@Input() set pdpToCopy(obs: Observable<number>) {
		this.pdpToCopySubject.registerSource(obs);
	}
	
	// Inputs correspondant a des changements induits par des actions utilisateur sur d'autre composants
	// NB: on utilise ici des Observable afin de rendre les changements le plus synchrone possible, ce que les bindings simple ne font pas
	// NB2: la classe SubjectRelay sert a simplifier le cas ou le parent changerait d'observable sur le binding au cours de la vie du composant
	private energySubject = new SubjectRelay<string | null>();
	@Input() set energieChangeCommand(obs: Observable<string | null>) {
		this.energySubject.registerSource(obs);
	}

	// Evenements de sortie du composant
	@Output() onModelChange = new EventEmitter<TraductionPdp[] | undefined>();

	private loadRisksForExistingPdp(pdpId: number, allTrads: TraductionPdp[]) {
		this.planPreventionService.getRisques(pdpId).subscribe(
			(data: PlanPreventionEntity) => {
				this.selectedTraductionIds = data.riskActivities != null ? data.riskActivities.map($ => $.traduction.id) : [];
				this.selectedTraductionIds.push.apply(
					this.selectedTraductionIds,
					data.risks != null ? data.risks.map($ => $.traduction.id) : []
				);
				this.selectedTraductionIds.push.apply(
					this.selectedTraductionIds,
					data.riskMesures != null ? data.riskMesures.map($ => $.traduction.id) : []
				);
				let refResponsibilities: RefResponsibilities = {};
				let autreByTradId: string[] = [];
				let poidsFemmeByTradId: number[] = [];
				let poidsHommeByTradId: number[] = [];
				data.riskMesures.forEach(mesurePrevention => {
					// pour chaque mp existant --> déterminer la responsibility (EU / EE / BOTH) et récupérer la valeur autre si elle est renseignée
					if (mesurePrevention.responsibility !== undefined) {
						refResponsibilities[mesurePrevention.traduction.id] = mesurePrevention.responsibility;
					}
					if (mesurePrevention.autre !== undefined) {
						autreByTradId[mesurePrevention.traduction.id] = mesurePrevention.autre;
					}
					if (mesurePrevention.poidsFemme) {
						poidsFemmeByTradId[mesurePrevention.traduction.id] = mesurePrevention.poidsFemme;
						this.poidsFemme = mesurePrevention.poidsFemme;
					}
					if (mesurePrevention.poidsHomme) {
						poidsHommeByTradId[mesurePrevention.traduction.id] = mesurePrevention.poidsHomme;
						this.poidsHomme = mesurePrevention.poidsHomme;
					}
				});
				allTrads.forEach(traduction => {
					traduction.activites = traduction.children.filter($ => $.type === 'ACTIVITE').map($ => {
						if (this.selectedTraductionIds.indexOf($.id) > -1) {
							$.selected = true;
						}
						$.updatable = false;
						return $;
					});
					traduction.riskOrImpacts = traduction.children.filter($ => $.type === this.type).map($ => {
						if (this.selectedTraductionIds.indexOf($.id) > -1) {
							$.selected = true;
						}
						$.updatable = false;
						return $;
					});
					traduction.mesures = traduction.children.filter($ => $.type === 'MESURE_PREVENTION').map($ => {
						if (this.selectedTraductionIds.indexOf($.id) > -1) {
							$.selected = true;
						}
						$.updatable = false;
						if (refResponsibilities[$.id] != null) {
							switch (refResponsibilities[$.id]) {
								case 'EU':
									$.chargeEntrepriseUtilisatrice = true;
									break;
								case 'EE':
									$.chargeEntrepriseExterne = true;
									break;
								case 'BOTH':
									$.chargeEntrepriseUtilisatrice = true;
									$.chargeEntrepriseExterne = true;
									break;
							}
						}
						autreByTradId[$.id] != null ? $.autre = autreByTradId[$.id] : null;
						poidsFemmeByTradId[$.id] != null ? poidsFemmeByTradId[$.id] : 15;
						poidsHommeByTradId[$.id] != null ? poidsHommeByTradId[$.id] : 25;
						return $;
					});
				});
				this.traductions = allTrads;
				this.onModelChange.emit(this.traductions);
			}
		);
	}

	private loadImpactsForExistingPdp(pdpId: number, allTrads: TraductionPdp[]) {
		this.planPreventionService.getImpacts(pdpId).subscribe(
			(data: PlanPreventionEntity) => {
				this.selectedTraductionIds = data.impactActivities != null ? data.impactActivities.map($ => $.traduction.id) : [];
				this.selectedTraductionIds.push.apply(
					this.selectedTraductionIds,
					data.impacts != null ? data.impacts.map($ => $.traduction.id) : []
				);
				this.selectedTraductionIds.push.apply(
					this.selectedTraductionIds,
					data.impactMesures != null ? data.impactMesures.map($ => $.traduction.id) : []
				);
				let refResponsibilities: RefResponsibilities = {};
				let autreByTradId: string[] = [];
				data.impactMesures.forEach(mesurePrevention => {
					// pour chaque mp existant --> déterminer la responsibility (EU / EE / BOTH) et récupérer la valeur autre si elle est renseignée
					if (mesurePrevention.responsibility !== undefined) {
						refResponsibilities[mesurePrevention.traduction.id] = mesurePrevention.responsibility;
					}
					if (mesurePrevention.autre !== undefined) {
						autreByTradId[mesurePrevention.traduction.id] = mesurePrevention.autre;
					}
				});
				allTrads.forEach(traduction => {
					traduction.activites = traduction.children.filter($ => $.type === 'ACTIVITE').map($ => {
						if (this.selectedTraductionIds.indexOf($.id) > -1) {
							$.selected = true;
						}
						$.updatable = false;
						return $;
					});
					traduction.riskOrImpacts = traduction.children.filter($ => $.type === this.type).map($ => {
						if (this.selectedTraductionIds.indexOf($.id) > -1) {
							$.selected = true;
						}
						$.updatable = false;
						return $;
					});
					traduction.mesures = traduction.children.filter($ => $.type === 'MESURE_PREVENTION').map($ => {
						if (this.selectedTraductionIds.indexOf($.id) > -1) {
							$.selected = true;
						}
						$.updatable = false;
						if (refResponsibilities[$.id] != null) {
							switch (refResponsibilities[$.id]) {
								case 'EU':
									$.chargeEntrepriseUtilisatrice = true;
									break;
								case 'EE':
									$.chargeEntrepriseExterne = true;
									break;
								case 'BOTH':
									$.chargeEntrepriseUtilisatrice = true;
									$.chargeEntrepriseExterne = true;
									break;
							}
						}

						autreByTradId[$.id] != null ? $.autre = autreByTradId[$.id] : null;
						return $;
					});
				});
				this.traductions = allTrads;
				this.onModelChange.emit(this.traductions);
			}
		);
	}

	onChangeActivite(activites: TraductionPdp[], associatedRiskOrImpacts: TraductionPdp[], associatedMps: TraductionPdp[]) {
		if (!activites.some($ => $.selected)) {
			// aucune activité selectionnée
			// décocher tous les autres risques
			associatedRiskOrImpacts.forEach(associatedTraduction => associatedTraduction.selected = false);
			this.onChangeRiskOrImpact(associatedRiskOrImpacts, associatedMps);
		}
		this.onModelChange.emit(this.traductions);
	}

	onChangeRiskOrImpact(traductions: TraductionPdp[], associatedMps: TraductionPdp[]) {
		if (!traductions.some($ => $.selected)) {
			// aucun risk ou impact selectionnée
			// décocher tous les autres mps
			associatedMps.filter(mp => !mp.traductionKey.trim().startsWith('AUTR')).forEach(mp => {
				mp.selected = false;
				// reinit du radio bouton responsibility
				mp.chargeEntrepriseExterne = false;
				mp.chargeEntrepriseUtilisatrice = false;
			});
		}
		this.onModelChange.emit(this.traductions);
	}

	noneSelected(associatedTraductions: TraductionPdp[]) {
		return !associatedTraductions.some($ => $.selected);
	}

	onChangeMesurePrevention(mesure: TraductionPdp, parent: TraductionPdp, index: number) {
		if ((!mesure.traductionKey.trim().startsWith('AUTR') && !mesure.selected) || (mesure.traductionKey.trim().startsWith('AUTR') && !mesure.fr)) {
			mesure.chargeEntrepriseExterne = false;
			mesure.chargeEntrepriseUtilisatrice = false;
		}
		if (!mesure.traductionKey.trim().startsWith('AUTR') && mesure.selected && mesure.fr.trim() === 'Autres' && index == parent.mesures.length -1) {
			// nous sommes sur le dernier "Autres" et on a coché la case de cette derniere mesure de prevention
			this.traductions.forEach(section => {
				if (section.id === parent.id) {
					let autreMesure = new TraductionPdp();
					autreMesure.traductionKey = 'EXTENDED_AUTR_1757_' + uuidv4();
					autreMesure.fr = 'Autres';
					autreMesure.type = 'MESURE_PREVENTION';
					autreMesure.parent = parent;
					autreMesure.categoriePdp = section.categoriePdp;
					parent.mesures.push(autreMesure);
					parent.children.push(autreMesure);
				}
			});
		}
		this.onModelChange.emit(this.traductions);
	}

	onChangePoidsFemme(mesure: TraductionPdp) {
		mesure.poidsFemme = this.poidsFemme;
		this.onModelChange.emit(this.traductions);
	}

	onChangePoidsHomme(mesure: TraductionPdp) {
		mesure.poidsHomme = this.poidsHomme;
		this.onModelChange.emit(this.traductions);
	}

	onChangeChargeEU(_mesure: TraductionPdp) {
		this.onModelChange.emit(this.traductions);
	}

	onChangeChargeEE(_mesure: TraductionPdp) {
		this.onModelChange.emit(this.traductions);
	}

	addOtherRiskOrImpactPdp = (traduction: TraductionPdp) => {
		//ACTIVITE
		let newIdActivite = traduction.activites.length > 0 ? traduction.activites[traduction.activites.length - 1].id + 1 : 1;
		let parentActivite = new TraductionPdp();
		parentActivite.id = traduction.id
		parentActivite.traductionKey = traduction.traductionKey;
		parentActivite.type = traduction.type;
		traduction.activites.push({
			activites: [],
			autre: "",
			chargeEntrepriseExterne: false,
			chargeEntrepriseUtilisatrice: false,
			children: [],
			en: "",
			es: "",
			fr: "",
			id: -newIdActivite,
			mesures: [],
			ordre: 0,
			parent: parentActivite,
			responsibility: "",
			riskOrImpacts: [],
			selected: false,
			traductionKey: parentActivite.traductionKey + '_ACT_' + newIdActivite,
			type: "ACTIVITE",
			updatable: true,
			poidsFemme: 15,
			poidsHomme: 25,
			categoriePdp: CategoriePdP.MIXTE // TODO
		});

		//RISK or IMPACT
		let newIdRiskImpact = newIdActivite + 1;
		let parentRiskOrImpact = new TraductionPdp();
		parentRiskOrImpact.id = traduction.id
		parentRiskOrImpact.traductionKey = traduction.traductionKey;
		let childRiskImpactTradKey = parentRiskOrImpact.traductionKey.concat(traduction.type === "PARENT_ANA_RISK" ? "_RSK_" : "_IMP_").concat(String(newIdRiskImpact));
		parentRiskOrImpact.type = traduction.type;
		traduction.riskOrImpacts.push({
			activites: [],
			autre: "",
			chargeEntrepriseExterne: false,
			chargeEntrepriseUtilisatrice: false,
			children: [],
			en: "",
			es: "",
			fr: "",
			id: -newIdRiskImpact,
			mesures: [],
			ordre: 0,
			parent: parentRiskOrImpact,
			responsibility: "",
			riskOrImpacts: [],
			selected: false,
			traductionKey: childRiskImpactTradKey,
			type: traduction.type === "PARENT_ANA_RISK" ? "RISK" : "IMPACT",
			updatable: true,
			poidsFemme: this.poidsFemme,
			poidsHomme: this.poidsHomme,
			categoriePdp: CategoriePdP.MIXTE,
		});

		//MESURES
		let newIdMesure = newIdRiskImpact + 1;
		let parentMesures = new TraductionPdp();
		parentMesures.id = traduction.id
		parentMesures.traductionKey = traduction.traductionKey;
		parentMesures.type = traduction.type;
		traduction.mesures.push({
			activites: [],
			autre: "",
			chargeEntrepriseExterne: false,
			chargeEntrepriseUtilisatrice: false,
			children: [],
			en: "",
			es: "",
			fr: "",
			id: -newIdMesure,
			mesures: [],
			ordre: 0,
			parent: parentMesures,
			responsibility: "",
			riskOrImpacts: [],
			selected: false,
			traductionKey: parentMesures.traductionKey + '_MP_' + newIdMesure,
			type: "MESURE_PREVENTION",
			updatable: true,
			poidsFemme: this.poidsFemme,
			poidsHomme: this.poidsHomme,
			categoriePdp: CategoriePdP.MIXTE,
		})
		this.onModelChange.emit(this.traductions);
	}

	removeTraductionAutre(traduction: TraductionPdp, id: number) {
		let indexToRemove = traduction.mesures.findIndex(mesure => mesure.id === id);
		if (indexToRemove !== -1) {
			traduction.mesures.splice(indexToRemove, 1);
			traduction.riskOrImpacts.splice(indexToRemove, 1);
			traduction.activites.splice(indexToRemove, 1);
		}
		this.onModelChange.emit(this.traductions);
	}

}